import type {IHttpResponse, IHttpService, IWindowService} from 'angular';
import type WalkerService from '../commons/services/walker.service';
import {CRITERION_OPERATORS} from './criterions/criterion-operators';
import OperatorableCriterionFactory from './criterion-factories/operatorable-criterion.factory';
import CriterionFactory from './criterion-factories/criterion.factory';
import CriterionsFormManager from './criterions-form-manager';
import OperatorableCriterionMarshaller from './criterion-marshallers/operatorable-criterion-marshaller';
import {responseToData, transferify} from 'shared/utils/data-service-utils';
import {identifiedValuesEquals} from 'shared/utils/custom-utils';
import type SearchHistoryDataService from './search-history.data-service';
import type {ViewableSearchField, SearchHistoryEntryDto} from 'typings/portaro.be.types';
import type {Criterion, OperatorableCriterion} from './criteria.types';
import type CurrentAuthService from '../commons/services/current-auth.service';
import {ngAsync} from 'shared/utils/ng-@decorators';
import KpVisitedPanel from '../commons/components/kp-visited-panel/KpVisitedPanel.svelte';
import KpFavouritesPanel from '../commons/components/kp-favourites-panel/KpFavouritesPanel.svelte';
import KpValueEditor from '../commons/value-editors/kp-value-editor/KpValueEditor.svelte';
import type {CriterionField} from './criteria.types';
import type {
    ValueEditorLabelContext
} from '../commons/value-editors/label/value-editor-label-context';
import {
    createValueEditorsLabelContext,
    LABEL_CONTEXT_KEY
} from '../commons/value-editors/label/value-editor-label-context';

declare global {
    interface Window {
        model: any & {
            searchFields: ViewableSearchField[];
            basicModeFieldsCount: number;
            searchParametersHistoryEnabled: boolean;
        }
    }
}

/**
 * @ngdoc controller
 * @name SearchFormController
 * @module portaro.search
 *
 * @description
 * Search form controller.
 */
export default class SearchFormController {
    public static controllerName = 'SearchFormController';

    public kpFavouritesPanelComponent = KpFavouritesPanel;
    public kpVisitedPanelComponent = KpVisitedPanel;
    public kpValueEditorComponent = KpValueEditor;

    public luceneQueryString: string;
    public restrictionString: string;
    public history: ImportableSearchHistoryEntryDto[];
    public OPERATORS: any;
    public expertMode: boolean;
    public searchFields: ViewableSearchField[]; // from window.model

    private criterionsFormManager: CriterionsFormManager;
    private operatorableCriterionMarshaller: OperatorableCriterionMarshaller;
    private searchParametersHistoryEnabled: boolean; // from window.model
    private basicModeFieldsCount: number; // from window.model

    /*@ngInject*/
    constructor(private $http: IHttpService,
                private CONTEXT_PATH: string,
                private walker: WalkerService,
                private searchHistoryDataService: SearchHistoryDataService,
                private $window: IWindowService,
                public currentAuthService: CurrentAuthService) {

        this.luceneQueryString = '';
        this.restrictionString = '';
        this.history = [];
        this.OPERATORS = CRITERION_OPERATORS;

        const operatorableCriterionFactory = new OperatorableCriterionFactory(new CriterionFactory());
        this.criterionsFormManager = new CriterionsFormManager(operatorableCriterionFactory);
        this.operatorableCriterionMarshaller = new OperatorableCriterionMarshaller(operatorableCriterionFactory);
    }

    public async init() {
        this.searchFields = this.$window.model.searchFields;
        this.basicModeFieldsCount = this.$window.model.basicModeFieldsCount;
        this.searchParametersHistoryEnabled = this.$window.model.searchParametersHistoryEnabled;

        this.setExpertModeAndFillCriterions(false);

        if (this.searchParametersHistoryEnabled) {
            const historyDto = await this.searchHistoryDataService.getAll();

            this.history = historyDto.map((dto: ImportableSearchHistoryEntryDto) => {
                dto.importable = !!dto.qt;
                return dto;
            });

            const firstImportableSearch = this.history.find((item) => item.importable);

            if (firstImportableSearch) {
                this.useFromHistory(firstImportableSearch);
            }
        }
    }

    public getCriterions() {
        return this.criterionsFormManager.getCriterions();
    }

    public addCriterion(field?) {
        return this.criterionsFormManager.addCriterion(field || this.searchFields[0]);
    }

    public async removeCriterion(criterion: Criterion) {
        this.criterionsFormManager.removeCriterion(criterion);
        await this.buildQueryIfAdmin();
    }

    public async updateCriterionByField(criterion, criterionField): Promise<void> {
        this.criterionsFormManager.updateCriterionByField(criterion, criterionField);
        await this.buildQueryIfAdmin();
    }

    public resetForm() {
        this.criterionsFormManager.clearCriterions();
        this.setExpertModeAndFillCriterions(false);
    }

    public async buildQueryIfAdmin(): Promise<void> {
        if (this.currentAuthService.hasRole('ROLE_ADMIN')) {
            await this.buildQuery();
        }
    }

    @ngAsync()
    public async buildQuery(): Promise<void> {
        const req = this.operatorableCriterionMarshaller.serialize(this.criterionsFormManager.getCriterions() as OperatorableCriterion[]);
        const transferableReq = transferify(req);
        this.restrictionString = JSON.stringify(transferableReq, null, 4);
        const queryData: IHttpResponse<string> = await this.$http.post(`${this.CONTEXT_PATH}/api/query-conversion?dialect=lucene`, transferableReq);
        this.luceneQueryString = responseToData(queryData);
    }

    public submitForm() {
        if (!this.isAnyCriterionFilled()) {
            return;
        }

        const req = this.operatorableCriterionMarshaller.serialize(this.criterionsFormManager.getCriterions() as OperatorableCriterion[]);
        this.walker.newPageOnContext('/search', {
            type: 'complex',
            qt: JSON.stringify(transferify(req))
        });
    }

    public useFromHistory(search: SearchHistoryEntryDto) {
        this.useRestriction(search.qt);
    }

    public isAnyCriterionFilled() {
        return this.criterionsFormManager.isAnyCriterionFilled();
    }

    public setExpertModeAndFillCriterions(enabled: boolean) {
        this.expertMode = enabled;

        if (enabled) { // filter out all empty fields (except first)
            this.criterionsFormManager.setCriterions(this.criterionsFormManager.getNotEmptyCriterions());
            if (this.criterionsFormManager.getCriterions().length === 0) {
                this.addCriterion();
            }
        } else { // add all missing fields (but limit 10 fields if empty)
            const criterionsFromSearchFields = this.searchFields.map((field) => this.criterionsFormManager.createCriterionByField(field));
            const prefilledCriterions = this.criterionsFormManager.getCriterions();

            const mergedCriterions = criterionsFromSearchFields.map((criterion) => {
                for(const prefilledCriterion of prefilledCriterions) {
                    if (identifiedValuesEquals(criterion.field, prefilledCriterion.field)) {
                        return prefilledCriterion;
                    }
                }

                return criterion;
            });

            // shrink to max fields count
            const criterions = mergedCriterions.slice(0, this.basicModeFieldsCount);

            this.criterionsFormManager.setCriterions(criterions);
        }
    }

    public createFieldLabelContext(field: CriterionField): ValueEditorLabelContext {
        return createValueEditorsLabelContext(field.id, field.text);
    }

    public setFieldLabelContext(fieldLabelContext: ValueEditorLabelContext): Map<string, any> {
        return new Map([[LABEL_CONTEXT_KEY, fieldLabelContext]]);
    }

    private useRestriction(restriction) {
        this.criterionsFormManager.setCriterions(this.operatorableCriterionMarshaller.deserialize(restriction));

        const isExpertMode = this.criterionsFormManager
                                 .getCriterions()
                                 .some(({operator}: OperatorableCriterion) => operator === this.OPERATORS.OR || operator === this.OPERATORS.AND_NOT);

        this.setExpertModeAndFillCriterions(isExpertMode);
    }
}

interface ImportableSearchHistoryEntryDto extends SearchHistoryEntryDto {
    importable: boolean;
}